import { createContext, useContext, useState, useEffect } from 'react';
import { authApi, motoApi } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [motoList, setMotoList] = useState([]);
  const [selectedMotoId, setSelectedMotoId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('vault_token');
    const savedUser = localStorage.getItem('vault_user');
    const savedMotoId = localStorage.getItem('vault_selected_moto');
    
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        if (savedMotoId) {
          setSelectedMotoId(savedMotoId);
        }
      } catch {
        localStorage.removeItem('vault_token');
        localStorage.removeItem('vault_user');
        localStorage.removeItem('vault_selected_moto');
      }
    }
    setLoading(false);
  }, []);

  // Load moto list when user is set
  useEffect(() => {
    if (user) {
      loadMotoList();
    }
  }, [user]);

  const loadMotoList = async () => {
    try {
      const response = await motoApi.getAll();
      setMotoList(response.data);
      // Auto-select first moto if none selected
      if (!selectedMotoId && response.data.length > 0) {
        const firstMotoId = response.data[0].id;
        setSelectedMotoId(firstMotoId);
        localStorage.setItem('vault_selected_moto', firstMotoId);
      }
    } catch (error) {
      console.error('Error loading moto list:', error);
    }
  };

  const selectMoto = (motoId) => {
    setSelectedMotoId(motoId);
    if (motoId) {
      localStorage.setItem('vault_selected_moto', motoId);
    } else {
      localStorage.removeItem('vault_selected_moto');
    }
  };

  const getSelectedMoto = () => {
    return motoList.find(m => m.id === selectedMotoId) || null;
  };

  const login = async (email, password) => {
    const response = await authApi.login({ email, password });
    const { token, user: userData } = response.data;
    
    localStorage.setItem('vault_token', token);
    localStorage.setItem('vault_user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const register = async (email, password, nome, paese = 'CH') => {
    const response = await authApi.register({ email, password, nome, paese });
    const { token, user: userData } = response.data;
    
    localStorage.setItem('vault_token', token);
    localStorage.setItem('vault_user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const updateUser = async (updates) => {
    const response = await authApi.updateMe(updates);
    const updatedUser = response.data;
    localStorage.setItem('vault_user', JSON.stringify(updatedUser));
    setUser(updatedUser);
    return updatedUser;
  };

  // Direct state update without API call
  const setUserState = (newUser) => {
    localStorage.setItem('vault_user', JSON.stringify(newUser));
    setUser(newUser);
  };

  // Refresh user from server
  const refreshUser = async () => {
    try {
      const response = await authApi.getMe();
      const userData = response.data;
      localStorage.setItem('vault_user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  // Get token
  const token = localStorage.getItem('vault_token');

  const logout = () => {
    localStorage.removeItem('vault_token');
    localStorage.removeItem('vault_user');
    localStorage.removeItem('vault_selected_moto');
    setUser(null);
    setMotoList([]);
    setSelectedMotoId(null);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading,
      token,
      login, 
      register, 
      logout,
      updateUser,
      setUserState,
      refreshUser,
      motoList,
      selectedMotoId,
      selectMoto,
      getSelectedMoto,
      refreshMotoList: loadMotoList
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
